import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import { FiArrowUp, FiImage, FiMic, FiX } from 'react-icons/fi';

import {
  ActionIcon,
  Box,
  Button,
  FileButton,
  Flex,
  Group,
  Image,
  showErrorNotification,
  TextArea,
  Tooltip,
} from '@highlight-cp/core-ui';
import type { TextAreaProps } from '@highlight-cp/core-ui';

import classes from './ai-textarea.module.css';
import { DEFAULT_ACCEPTED_FILE_TYPES, PERSONA_CHAT_ATTACHMENT_OVERFLOW_MESSAGE } from './constants';
import FileDropOverlay from './file-drop-overlay';
import {
  useAttachments,
  isFileAccepted,
  useFileDrop,
  useHandlers,
  usePlaceholder,
  useVoiceInput,
} from './hooks';
import { hasAiTextareaSubmitContent } from './hooks/use-handlers/use-handlers';
import type { AiTextareaSubmitPayload } from './types';
import { resizePersonaChatImageFile } from './utils/resize-persona-chat-image-file';

export interface AiTextareaHandle {
  addFiles: (files: File | File[] | null) => Promise<void>;
}

interface AiTextareaProps extends Omit<TextAreaProps, 'onKeyDown' | 'onSubmit'> {
  onSubmit: (payload: AiTextareaSubmitPayload) => void;
  placeholders?: string[];
  /** Labels the submit control, swapping the arrow-up icon button for a labelled button. */
  submitLabel?: string;
  /**
   * Content pinned opposite the actions inside the input shell. Passing it moves the actions onto
   * their own row under the textarea — there is no room for it beside an inline arrow button.
   */
  submitRowStart?: ReactNode;
  value?: string;
  voiceInputLanguage?: string;
  isSubmitting?: boolean;
  clearOnSubmit?: boolean;
  hideVoiceButton?: boolean;
  allowImageUpload?: boolean;
  requireTextWithImageAttachments?: boolean;
  accept?: string;
  maxFiles?: number;
  externalDropZone?: boolean;
  onDropZoneAvailabilityChange?: (canAcceptFiles: boolean) => void;
}

const PREVIEW_SIZE = 36;

const AiTextareaAttachmentPreviews = ({
  files,
  onRemove,
}: {
  files: File[];
  onRemove: (index: number) => void;
}) => {
  const { t } = useTranslation();

  const previewUrls = useMemo(() => files.map((file) => URL.createObjectURL(file)), [files]);

  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  if (files.length === 0) {
    return null;
  }

  return (
    <Flex className={classes.previews} gap="xs" wrap="nowrap">
      {files.map((file, index) => (
        <Box key={`${file.name}-${index}`} className={classes.previewItem}>
          <Image
            src={previewUrls[index]}
            alt={file.name}
            width={PREVIEW_SIZE}
            height={PREVIEW_SIZE}
            radius="sm"
          />
          <ActionIcon
            aria-label={t('Remove {{name}}', { name: file.name })}
            mod="ai-textarea-preview-remove"
            onClick={() => onRemove(index)}
            pos="absolute"
            right="calc(-1 * var(--ai-textarea-preview-remove-offset))"
            top="calc(-1 * var(--ai-textarea-preview-remove-offset))"
            type="button"
          >
            <FiX />
          </ActionIcon>
        </Box>
      ))}
    </Flex>
  );
};

const AiTextarea = forwardRef<AiTextareaHandle, AiTextareaProps>((props, ref) => {
  const { t } = useTranslation();
  const {
    onChange,
    onSubmit,
    placeholders,
    voiceInputLanguage = 'en-US',
    isSubmitting = false,
    clearOnSubmit = true,
    minRows = 2,
    hideVoiceButton = false,
    allowImageUpload = false,
    requireTextWithImageAttachments = false,
    accept = DEFAULT_ACCEPTED_FILE_TYPES,
    maxFiles = 1,
    externalDropZone = false,
    onDropZoneAvailabilityChange,
    submitLabel,
    submitRowStart,
    ...restProps
  } = props;

  const { label, description, error: fieldError, id, ...textAreaProps } = restProps;
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const errorId = `${textareaId}-error`;
  const descriptionId = `${textareaId}-description`;
  const hasFieldError = Boolean(fieldError) && typeof fieldError !== 'boolean';
  const hasDescription = Boolean(description);
  const describedBy = useMemo(() => {
    const ids = [hasFieldError ? errorId : null, hasDescription ? descriptionId : null].filter(
      (value): value is string => value != null,
    );

    return ids.length > 0 ? ids.join(' ') : undefined;
  }, [descriptionId, errorId, hasDescription, hasFieldError]);

  // Mantine InputBase supports `withAria`; it is omitted from core-ui's published TextAreaProps.
  const textareaAccessibilityProps = {
    withAria: false,
    'aria-describedby': describedBy,
    'aria-errormessage': hasFieldError ? errorId : undefined,
    'aria-invalid': hasFieldError ? true : undefined,
  } as TextAreaProps;

  const fileButtonResetRef = useRef<() => void>(null);
  const isPreparingAttachmentsRef = useRef(false);
  const [isPreparingAttachments, setIsPreparingAttachments] = useState(false);

  const { files, addFiles, removeFile, clearFiles } = useAttachments({ accept, maxFiles });

  const {
    handleChange,
    handleSubmit,
    handleKeyDown,
    value,
    setValue,
    handleFocus,
    handleBlur,
    isFocused,
  } = useHandlers({
    initialValue: textAreaProps.value,
    onSubmit,
    clearOnSubmit,
    files,
    clearFiles,
    isSubmitting,
    isPreparingAttachments,
    isSubmitBlocked: () => isPreparingAttachmentsRef.current,
    requireTextWithImageAttachments,
  });

  const composedHandleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      handleChange(event);
      onChange?.(event);
    },
    [handleChange, onChange],
  );

  const placeholder = usePlaceholder({
    placeholders,
    placeholder: textAreaProps.placeholder,
  });

  const handleTranscript = useCallback(
    (text: string) => {
      setValue((prev) => (prev ? `${prev} ${text}` : text));
    },
    [setValue],
  );

  const {
    isListening,
    isSupported,
    isMicrophoneAvailable,
    startListening,
    stopListening,
    interimTranscript,
    error: voiceError,
  } = useVoiceInput({
    onTranscript: handleTranscript,
    language: voiceInputLanguage,
  });

  const handleVoiceClick = () => {
    if (isListening) {
      void stopListening();
    } else {
      void startListening();
    }
  };
  const isVoiceDisabled = !isSupported || !isMicrophoneAvailable;

  const voiceTooltip = useMemo((): string | undefined => {
    if (!isSupported) {
      return t('Voice input is not supported in this browser. Try using Chrome or Edge.');
    }
    if (!isMicrophoneAvailable) {
      return t(
        'Microphone access denied. Click the lock icon in your browser address bar to enable microphone permissions.',
      );
    }
    if (voiceError) {
      return t('Voice recognition failed. Please try again.');
    }
    return undefined;
  }, [isSupported, isMicrophoneAvailable, voiceError, t]);

  const displayValue =
    isListening && interimTranscript
      ? value
        ? `${value} ${interimTranscript}`
        : interimTranscript
      : value;

  const hasSubmitContent = hasAiTextareaSubmitContent({
    text: value ?? '',
    fileCount: files.length,
    requireTextWithImageAttachments,
  });
  const isSubmitDisabled = !hasSubmitContent || isSubmitting || isPreparingAttachments;
  const isAttachDisabled = files.length >= maxFiles;
  const showVoiceButton = !hideVoiceButton;
  const showAttachButton = allowImageUpload;

  const processIncomingFiles = useCallback(
    async (selected: File | File[] | null) => {
      if (!selected || isPreparingAttachmentsRef.current) {
        return;
      }

      const candidates = Array.isArray(selected) ? selected : [selected];
      let resizeFailedCount = 0;
      let processed = candidates;

      if (allowImageUpload) {
        isPreparingAttachmentsRef.current = true;
        setIsPreparingAttachments(true);

        try {
          processed = (
            await Promise.all(
              candidates.map(async (file) => {
                if (!isFileAccepted(file, accept)) {
                  return file;
                }

                try {
                  return await resizePersonaChatImageFile(file);
                } catch {
                  resizeFailedCount += 1;
                  return null;
                }
              }),
            )
          ).filter((file): file is File => file != null);
        } finally {
          isPreparingAttachmentsRef.current = false;
          setIsPreparingAttachments(false);
        }
      }

      if (processed.length === 0) {
        if (resizeFailedCount > 0) {
          showErrorNotification({
            message: t('Could not process one or more images.'),
          });
        }

        fileButtonResetRef.current?.();
        return;
      }

      const filesToAdd = processed.length === 1 ? (processed[0] as File) : processed;
      const { rejected, overflowCount } = addFiles(filesToAdd);

      if (resizeFailedCount > 0) {
        showErrorNotification({
          message: t('Could not process one or more images.'),
        });
      } else if (rejected.length > 0) {
        showErrorNotification({
          message: t('Only image files are supported.'),
        });
      } else if (overflowCount > 0) {
        showErrorNotification({
          message: t(PERSONA_CHAT_ATTACHMENT_OVERFLOW_MESSAGE, { count: maxFiles }),
        });
      }

      fileButtonResetRef.current?.();
    },
    [accept, addFiles, allowImageUpload, maxFiles, t],
  );

  useImperativeHandle(
    ref,
    () => ({
      addFiles: processIncomingFiles,
    }),
    [processIncomingFiles],
  );

  useEffect(() => {
    if (!externalDropZone || !allowImageUpload) {
      return;
    }

    onDropZoneAvailabilityChange?.(!isAttachDisabled && !isSubmitting && !isPreparingAttachments);
  }, [
    allowImageUpload,
    externalDropZone,
    isAttachDisabled,
    isPreparingAttachments,
    isSubmitting,
    onDropZoneAvailabilityChange,
  ]);

  const handleFileChange = (selected: File | File[] | null) => {
    processIncomingFiles(selected);
  };

  const useInternalDropZone = allowImageUpload && !externalDropZone;

  const isAttachmentInputDisabled =
    isAttachDisabled || isSubmitting || isPreparingAttachments || Boolean(textAreaProps.disabled);

  const { isDragOver, dropHandlers } = useFileDrop({
    enabled: useInternalDropZone,
    disabled: isAttachmentInputDisabled,
    onFiles: processIncomingFiles,
  });

  // A labelled submit or a start slot needs the room of its own row, so the actions drop below the
  // textarea instead of sitting beside it.
  const hasActionRow = Boolean(submitLabel || submitRowStart);

  const actions = (
    <Group className={classes.rightActions} gap="xs" wrap="nowrap">
      {showVoiceButton && (
        <Tooltip label={voiceTooltip} disabled={!isVoiceDisabled && !voiceError}>
          <ActionIcon
            aria-label={isListening ? t('Stop voice input') : t('Start voice input')}
            disabled={isVoiceDisabled}
            mod={{ 'ai-textarea-action': true, 'ai-textarea-listening': isListening }}
            onClick={handleVoiceClick}
            type="button"
          >
            <FiMic size={20} strokeWidth={2.5} />
          </ActionIcon>
        </Tooltip>
      )}
      {showAttachButton && (
        <FileButton
          accept={accept}
          disabled={isAttachmentInputDisabled}
          onChange={handleFileChange}
          resetRef={fileButtonResetRef}
        >
          {(fileButtonProps) => (
            <ActionIcon
              aria-busy={isPreparingAttachments}
              aria-label={isPreparingAttachments ? t('Preparing image…') : t('Attach image')}
              disabled={isAttachmentInputDisabled}
              loading={isPreparingAttachments}
              mod="ai-textarea-action"
              onClick={() => fileButtonProps.onClick()}
              type="button"
            >
              <FiImage size={20} strokeWidth={2.5} />
            </ActionIcon>
          )}
        </FileButton>
      )}
      {submitLabel ? (
        <Button
          disabled={!hasSubmitContent || isPreparingAttachments}
          loading={isSubmitting}
          size="xs"
          type="submit"
        >
          {submitLabel}
        </Button>
      ) : (
        <ActionIcon
          aria-label={t('Submit')}
          disabled={isSubmitDisabled}
          color={hasSubmitContent && !isSubmitDisabled ? 'primary' : undefined}
          loading={isSubmitting}
          mod="ai-textarea-submit"
          size="md"
          type="submit"
          variant={hasSubmitContent && !isSubmitDisabled ? 'filled' : 'subtle'}
        >
          <FiArrowUp strokeWidth={2.5} />
        </ActionIcon>
      )}
    </Group>
  );

  return (
    <Box
      className={clsx(classes.root, useInternalDropZone && isDragOver && classes.dragOver)}
      {...(useInternalDropZone ? dropHandlers : {})}
    >
      {useInternalDropZone && isDragOver && <FileDropOverlay maxFiles={maxFiles} />}
      <form className={classes.form} onSubmit={handleSubmit}>
        {label ? (
          <label className={classes.fieldLabel} htmlFor={textareaId}>
            {label}
          </label>
        ) : null}
        <Box className={classes.inputShell}>
          <Flex className={classes.inputContent} direction="column" gap="xs">
            {showAttachButton && (
              <AiTextareaAttachmentPreviews files={files} onRemove={removeFile} />
            )}
            <Flex align="center" className={classes.inputRow} gap="xs">
              <Box className={classes.textAreaField}>
                <TextArea
                  {...textAreaProps}
                  {...textareaAccessibilityProps}
                  autosize
                  id={textareaId}
                  value={displayValue}
                  onChange={composedHandleChange}
                  onKeyDown={handleKeyDown}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  minRows={minRows}
                  // A read-only field takes no typing, so its placeholder never competes with text.
                  placeholder={isFocused && !textAreaProps.readOnly ? '' : placeholder}
                />
              </Box>
              {!hasActionRow && actions}
            </Flex>
            {/* `mt` on top of the column's `gap`: the design sets the action row further off the
                text than the attachment previews sit from each other. */}
            {hasActionRow && (
              <Group
                gap="xs"
                justify={submitRowStart ? 'space-between' : 'flex-end'}
                mt="xs"
                wrap="nowrap"
              >
                {submitRowStart}
                {actions}
              </Group>
            )}
          </Flex>
        </Box>
        {hasFieldError ? (
          <Box id={errorId} className={classes.fieldError}>
            {fieldError}
          </Box>
        ) : null}
        {hasDescription ? (
          <Box id={descriptionId} className={classes.fieldDescription}>
            {description}
          </Box>
        ) : null}
      </form>
    </Box>
  );
});

AiTextarea.displayName = 'AiTextarea';

export default AiTextarea;
